<script>
  import { onMount, onDestroy } from 'svelte';
  import { modifyPlan, registerPlan } from '@lib/api/plan';

  export let mode = 'create';       // 'create' = 등록, 'modify' = 수정
  export let initialData = {        // 수정 시 전달받는 초기 데이터
    plan_id: '',
    plan_name: '',
    amount: '',
    currency: '',
    period: '',
    memo: '',
    order: 0,
    descriptions: [{ checked: false, text: '' }]
  };    
  export let onClose = () => {};    // 팝업 닫기 콜백
  export let onSave = () => {};     // 저장 완료 콜백

  let isCurrencyOpen = false;       // 통화 선택 드롭다운 열림 여부
  let isPeriodOpen = false;         // 이용기간 선택 드롭다운 열림 여부
  let currencyDropdownEl = null;    // 통화 드롭다운 DOM 요소 참조
  let periodDropdownEl = null;      // 이용 기간 드롭다운 DOM 요소 참조
  let viewCurrency = '';            // 사용자에게 표시할 통화명
  let viewPeriod = '';              // 사용자에게 표시할 이용 기간
  let warningMessage = '';          // 공통 경고 메시지

  $: actionLabel = mode === 'create' ? '저장하기' : '수정하기';

  // 개별 체크 상태 변화 감지 후 전체 선택 상태 갱신
  $: selectAll = formData.descriptions.length > 0 &&
    formData.descriptions.every(d => d.checked);

  // 폼 상태 정의
  let formData = {
    plan_name: '',        // 플랜명
    amount: '',          // 금액
    currency: '',        // 통화 선택 (ex: KRW, USD)
    period: '',          // 이용기간 선택 (ex: day, month, year)
    memo: '',            // 메모
    order: 0,            // 순서
    descriptions: [      // 플랜 설명 목록
      { checked: false, text: '' },
      { checked: false, text: '' },
      { checked: false, text: '' }
    ]
  };

  // 저장 처리 함수
  async function handleSubmit() {
    // 유효성 검사 시작
    if (!formData.plan_name.trim()) {
      warningMessage = '플랜명을 입력해주세요.';
      return;
    }

    if (!/^\d+$/.test(formData.amount)) {
      warningMessage = '금액은 숫자로 입력해주세요.';
      return;
    }

    if (!formData.currency) {
      warningMessage = '통화를 선택해주세요.';
      return;
    }

    if (!formData.period) {
      warningMessage = '이용기간을 선택해주세요.';
      return;
    }

    if (!formData.order || formData.order==0) {
      warningMessage = '순서를 지정해주세요.'
      return;
    }


    if (formData.descriptions.length === 0) {
      warningMessage = '플랜 설명은 최소 1개 이상 입력해야 합니다.';
      return;
    }

    // 설명 항목 중 하나라도 비어있으면 오류
    const hasEmptyDesc = formData.descriptions.some(desc => !desc.text.trim());
    if (hasEmptyDesc) {
      warningMessage = '모든 플랜 설명을 입력해주세요.';
      return;
    }

    // 유효성 통과 시
    warningMessage = ''; // 경고 초기화
    try {
      if (mode === 'create') {
        const result = await registerPlan(formData);
        console.log('등록 성공:', result);
      } else if (mode === 'modify') {
        const result = await modifyPlan(formData);
        console.log('수정 성공:', result);
      }

      onSave();
    }
     catch (e) {
      console.error('저장 실패:', e);
      warningMessage = e.message || '저장 중 오류가 발생했습니다.';
    }
  }

  // 선택된 설명 항목 삭제
  function handleRemoveDescriptions() {
    const selected = formData.descriptions.filter(item => item.checked);
    if (selected.length === 0) {
      // 아무 항목도 선택되지 않은 경우 경고 메시지 출력
      warningMessage = '삭제할 설명을 선택해주세요.';
      return;
    }
    // 선택되지 않은 항목만 남기고 갱신
    formData.descriptions = formData.descriptions.filter(item => !item.checked);
    warningMessage = ''; // 성공적으로 삭제되었으면 경고 문구 제거
  }

  // 설명 항목 추가
  function handleAddDescription() {
    // 새 설명 항목을 리스트에 추가 (초기값: 체크 안됨, 텍스트 비어 있음)
    formData.descriptions = [
      ...formData.descriptions,
      { checked: false, text: '' }
    ];
  }

  // 전체 선택/해제 토글
  function toggleSelectAll() {
    // 전체 항목의 checked 상태를 selectAll 값으로 일괄 설정
    formData.descriptions = formData.descriptions.map(d => ({
      ...d,
      checked: selectAll
    }));
  }

  // 통화 드롭다운 열기/닫기 토글
  function toggleCurrencyDropdown() {
    isCurrencyOpen = !isCurrencyOpen;
  }

  // 기간 드롭다운 열기/닫기 토글
  function togglePeriodDropdown() {
    isPeriodOpen = !isPeriodOpen;
  }

  /**
   * 통화 선택 시 호출됨
   * @param {string} _currency 실제 사용될 통화 값 (예: 'USD')
   * @param {string} _viewCurrency 사용자에게 보여줄 통화명 (예: '달러')
   */
  function selectCurrency(_currency, _viewCurrency) {
    formData.currency = _currency;
    viewCurrency = _viewCurrency;
    isCurrencyOpen = false; // 선택 후 드롭다운 닫기
  }


  /**
   * 기간 선택 시 호출됨
   * @param {string} _period 실제 사용될 기간 값 (예: 'day, month, year')
   * @param {string} _viewPeriod 사용자에게 보여줄 기간명 (예: '일, 월 ,년')
   */
  function selectPeriod(_period, _viewPeriod) {
    formData.period = _period;
    viewPeriod = _viewPeriod;
    isPeriodOpen = false; // 선택 후 드롭다운 닫기
  }

  /**
   * 드롭다운 외부 클릭 시 드롭다운을 닫기 위한 이벤트 핸들러
   * @param {{ target: any; }} event 클릭 이벤트 객체
   */
  function handleClickOutside(event) {
    // 통화 드롭다운 외부 클릭 감지
    if (isCurrencyOpen && currencyDropdownEl && !currencyDropdownEl.contains(event.target)) {
      isCurrencyOpen = false;
    }

    // 기간 드롭다운 외부 클릭 감지
    if (isPeriodOpen && periodDropdownEl && !periodDropdownEl.contains(event.target)) {
      isPeriodOpen = false;
    }
  }

  onMount(() => {
    document.addEventListener('click', handleClickOutside);
    
    if (mode === 'modify' && initialData) {
        formData = {
          plan_id: initialData._id || '',
          plan_name: initialData.plan_name || '',
          amount: initialData.amount || '',
          currency: initialData.currency || '',
          period: initialData.period || '',
          memo: initialData.memo || '',
          order: initialData.order || 0,
          descriptions: initialData.descriptions || [{ checked: false, text: '' }]
        };

        // 보기용 텍스트 세팅
        viewCurrency = initialData.currency === 'KRW' ? '원'
                    : initialData.currency === 'USD' ? '달러' : '';

        viewPeriod = initialData.period === 'day' ? '일'
                  : initialData.period === 'month' ? '월'
                  : initialData.period === 'year' ? '년' : '';
      }
  });

  onDestroy(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<div class="popup-wrap">
  <div class="popup-overlay" role="presentation" aria-label="팝업 닫기" on:click={onClose}></div>

  <section class="popup-container">
    <div class="popup-container__inner d-flex" style="max-width:65.2rem;">
      <div class="d-flex flex-column w-100">

        <!-- Header -->
        <div class="popup-header">
          <div class="d-flex align-center w-100">
            <p class="font-20 font-weight-700 line-height-2">
              {mode === 'create' ? '플랜 등록하기' : '플랜 수정하기'}
            </p>
            <button type="button" class="btn-close ml-auto"   aria-label="닫기" on:click={onClose}>
              <i class="icon icon-close"></i>
            </button>
          </div>
        </div>

        <!-- Content -->
        <div class="popup-cont">
          <div class="d-flex h-100">
              <div class="prompt-block">
                  <div class="table-wrap flex-1">
                      <table class="table-basic table-border table-basic-playApply1">
                          <caption>플랜 등록하기 테이블</caption>
                          <colgroup>
                              <col style="width:20%">
                              <col style="width:auto">
                          </colgroup>
                          <tbody>
                          <tr>
                              <th scope="row">플랜명</th>
                              <td>
                                  <form action="" class="d-flex align-center flex-wrap">
                                      <fieldset class="w-100">
                                          <legend>플랜 등록 input 텍스트</legend>
                                          <label for="input-field-planApply1">
                                              <input
                                                type="text"
                                                id="input-field-planApply1"
                                                class="input-field h-40"
                                                placeholder=""
                                                bind:value={formData.plan_name}
                                              />
                                          </label>
                                      </fieldset>
                                  </form>
                              </td>
                          </tr>
                          <tr>
                              <th scope="row">금액</th>
                              <td>
                                  <form action="" class="d-flex align-center flex-wrap">
                                      <fieldset class="w-100">
                                          <legend>플랜 등록 input 텍스트</legend>
                                          <label for="input-field-planApply2">
                                              <input
                                                type="text"
                                                id="input-field-planApply2"
                                                class="input-field h-40"
                                                placeholder=""
                                                bind:value={formData.amount}
                                              />
                                          </label>
                                      </fieldset>
                                  </form>
                              </td>
                          </tr>
                          <tr>
                              <th scope="row">단위</th>
                              <td>
                                  <div class="d-flex align-center">
                                      <form action="" class="d-flex align-center flex-wrap">
                                          <fieldset class="width-170">
                                              <legend>플랜 통화선택 input 셀렉트</legend>
                                              <div class="custom-select w-100" class:active={isCurrencyOpen} bind:this={currencyDropdownEl}>
                                                <button type="button" class="custom-select__selected" on:click={toggleCurrencyDropdown}>
                                                      <span>{viewCurrency || '통화 선택'}</span>
                                                      <i class="icon icon-dropdown"></i>
                                                </button>
                                                <ul class="custom-select__options">
                                                  <li class="custom-select__option"> 
                                                    <button type="button" class="custom-select__button" on:click={() => selectCurrency('KRW','원')}>
                                                      원
                                                    </button>
                                                  </li>
                                                  <li class="custom-select__option">
                                                    <button type="button" class="custom-select__button" on:click={() => selectCurrency('USD','달러')}>
                                                      달러
                                                    </button>
                                                  </li>
                                                </ul>
                                              </div>
                                          </fieldset>
                                      </form>
                                      <span class="mx-3">/</span>
                                      <form action="" class="d-flex align-center flex-wrap">
                                      <fieldset class="width-170">
                                          <legend>플랜 이용기간 선택 input 셀렉트</legend>
                                          <div class="custom-select w-100" class:active={isPeriodOpen} bind:this={periodDropdownEl}>
                                              <button type="button" class="custom-select__selected" on:click={togglePeriodDropdown}>
                                                    <span>{viewPeriod || '이용기간 선택'}</span>
                                                    <i class="icon icon-dropdown"></i>
                                              </button>
                                                <ul class="custom-select__options">
                                                  <li class="custom-select__option"> 
                                                    <button type="button" class="custom-select__button" on:click={() => selectPeriod('day','일')}>
                                                      일
                                                    </button>
                                                  </li>
                                                  <li class="custom-select__option">
                                                    <button type="button" class="custom-select__button" on:click={() => selectPeriod('month','월')}>
                                                      월
                                                    </button>
                                                  </li>
                                                  <li class="custom-select__option">
                                                    <button type="button" class="custom-select__button" on:click={() => selectPeriod('year','년')}>
                                                      년
                                                    </button>
                                                  </li>
                                                </ul>
                                          </div>
                                      </fieldset>
                                  </form>
                                  </div>
                              </td>
                          </tr>
                          <tr>
                              <th scope="row">메모</th>
                              <td>
                                  <form action="" class="d-flex align-center flex-wrap">
                                      <fieldset class="w-100">
                                          <legend>플랜 등록 input 텍스트</legend>
                                          <label for="input-field-planApply3">
                                              <input
                                                type="text"
                                                id="input-field-planApply3"
                                                class="input-field h-40"
                                                placeholder=""
                                                bind:value={formData.memo}
                                              />
                                          </label>
                                      </fieldset>
                                  </form>
                              </td>
                          </tr>
                          <tr>
                              <th scope="row">순서</th>
                              <td>
                                  <form action="" class="d-flex align-center flex-wrap">
                                      <fieldset class="w-100">
                                          <legend>플랜 등록 input 텍스트</legend>
                                          <label for="input-field-planApply4">
                                              <input
                                                type="text"
                                                id="input-field-planApply4"
                                                class="input-field h-40"
                                                placeholder=""
                                                bind:value={formData.order}
                                              />
                                          </label>
                                      </fieldset>
                                  </form>
                              </td>
                          </tr>
                          </tbody>
                      </table>
                  </div>
                  <div class="d-flex align-center mt-6">
                      <p class="font-16">플랜설명</p>
                      <button type="button" class="btn ml-auto" on:click={handleRemoveDescriptions}>설명삭제</button>
                      <button type="button" class="btn btn bg-darkgray ml-2" on:click={handleAddDescription}>설명추가</button>
                  </div>
                  <div class="table-wrap mt-2 flex-1">
                      <table class="table-basic table-border table-basic-playApply1">
                          <caption>플랜 등록하기 테이블</caption>
                          <colgroup>
                              <col style="width:10%">
                              <col style="width:auto">
                          </colgroup>
                          <thead>
                          <tr>
                              <th scope="col">
                                  <div class="custom-checkBox">
                                      <form action="">
                                          <fieldset>
                                              <legend></legend>
                                              <input
                                                type="checkbox"
                                                id="input-plan-checkboxAll"
                                                class="input-checkBoxAll"
                                                title="플랜설명 입력 전체 checkbox"
                                                bind:checked={selectAll}
                                                on:change={toggleSelectAll}
                                              />
                                              <label for="input-plan-checkboxAll">
                                                  <span>플랜설명 입력 전체 checkbox</span>
                                              </label>
                                          </fieldset>
                                      </form>
                                  </div>
                              </th>
                              <th scope="col">플랜 설명</th>
                          </tr>
                          </thead>
                          <tbody>
                          {#each formData.descriptions as desc, i (i)}
                          <tr>
                            <td>
                              <div class="custom-checkBox text-center">
                                <form action="">
                                  <fieldset>
                                    <legend></legend>
                                    <input
                                      type="checkbox"
                                      id={"input-plan-checkbox" + i}
                                      class="input-checkBox"
                                      bind:checked={desc.checked}
                                      title={`플랜설명 입력 체크박스${i + 1}`}
                                    />
                                    <label for={"input-plan-checkbox" + i}>
                                      <span>{`플랜설명 입력 체크박스${i + 1}`}</span>
                                    </label>
                                  </fieldset>
                                </form>
                              </div>
                            </td>
                            <td>
                              <form action="" class="d-flex align-center flex-wrap">
                                <fieldset class="w-100">
                                  <legend>플랜설명 input 텍스트</legend>
                                  <label for={"input-field-planExplain" + i}>
                                    <input
                                      type="text"
                                      id={"input-field-planExplain" + i}
                                      class="input-field h-40"
                                      title="플랜설명 input 텍스트"
                                      placeholder=""
                                      bind:value={desc.text}
                                    />
                                  </label>
                                </fieldset>
                              </form>
                            </td>
                          </tr>
                          {/each}
                          </tbody>
                      </table>
                  </div>
                  {#if warningMessage}
                    <p class="d-flex align-center mt-3 font-13 color-red">
                      <i class="icon icon-calmMark-red mr-1"></i>
                      {warningMessage}
                    </p>
                  {/if}
              </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="popup-footer">
          <div class="btn-wrap justify-center" style="border-top:none;">
            <button type="button" class="btn border-blue" on:click={onClose}>취소</button>
            <button type="button" class="btn bg-blue ml-2" on:click={handleSubmit}>{actionLabel}</button>
          </div>
        </div>

      </div>
    </div>
  </section>
</div>